// @ts-nocheck
import { useVisibleScheduleTabs } from '@/components/public/EventScheduleTabs';
// Utility function to extract `src` from an iframe string
function extractSrcFromIframe(iframeHtml: string): string | null {
  const match = iframeHtml.match(/src=["']([^"']+)["']/);
  return match ? match[1] : null;
}

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Calendar, ExternalLink, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import EventPromoVideo, { parseYouTubeId } from '@/components/events/EventPromoVideo';
import EventHelpfulInformation from '@/components/events/EventHelpfulInformation';
import EventSidebar from '@/components/events/EventSidebar';
import EventCoachesSection from '@/components/events/EventCoachesSection';
import PurchasePacketsModal from '@/components/coach-packets/PurchasePacketsModal';
import EventVerificationNotice from '@/components/events/EventVerificationNotice';

import EventScheduleModal from '@/components/events/EventScheduleModal';
import { convertYouTubeToEmbed, isYouTubeUrl, extractYouTubeId } from '@/lib/utils';
import EventFAQSection from '@/components/events/EventFAQSection';
import EventTournamentSchedule from '@/components/events/EventTournamentSchedule';
import OfficialHotelsSection from '@/components/events/OfficialHotelsSection';
import { sortDivisions } from '@/utils/divisionSort';
import SponsorLogo from '@/components/sponsors/SponsorLogo';

interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface EventSponsor {
  id: string;
  name: string;
  logo_url?: string;
  website_url?: string;
}

interface EventData {
  id: string;
  slug: string;
  name: string;
  description?: string;
  start_date: string;
  end_date?: string;
  registration_close_date?: string;
  status: string;
  thumbnail_url?: string;
  background_image_url?: string;
  gender?: string;
  sport_id?: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  venue_address?: string;
  venue_zip?: string;
  main_venue_id?: string;
  additional_venue_ids?: string[];
  check_in_instructions_html?: string;
  checkin_instructions?: string;
  promo_video_url?: string;
  age_groups_description?: string;
  pricing_notes?: string;
  event_format?: string;
  hotel_booking_url?: string;
  live_streaming_url?: string;
  rules?: string;
  media_credentials_description?: string;
  media_credentials_form_id?: string;
  media_credentials_notification_email?: string;
  ticket_display_text?: string;
  coaches_external_url?: string;
  more_info?: string;
  coach_attendance_fee?: number;
  media_credentials_price?: number;
  accept_online_registrations?: boolean;
  external_registration_url?: string;
  main_venue?: Venue;
  additional_venues?: Venue[];
  event_category_mappings: Array<{
    event_categories: {
      id: string;
      name: string;
    };
  }>;
  age_groups?: Array<{
    id: string;
    name: string;
    registration_fee: string;
  }>;
  pricing_tiers?: Array<{
    id: string;
    tier_name: string;
    price: string;
  }>;
  gallery_images?: string[];
  // NCAA Live fields
  is_ncaa_live_event?: boolean;
  ncaa_d1_first_coach_fee?: number;
  ncaa_d1_additional_coach_fee?: number;
  ncaa_other_first_coach_fee?: number;
  ncaa_other_additional_coach_fee?: number;
  ncaa_hospitality_days?: string[];
  coach_coupon_code?: string;
  ncaa_coupon_code?: string;
  ncaa_coupon_discount_type?: 'percentage' | 'fixed';
  ncaa_coupon_discount_value?: number;
  coach_registration_instructions?: string;
}

const EventDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { profile } = useAuth();
  const [event, setEvent] = useState<EventData | null>(null);
  const [sponsors, setSponsors] = useState<EventSponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [publishedPacket, setPublishedPacket] = useState<{ id: string; packet_price_cents: number; packet_file_url?: string } | null>(null);
  const [showPacketModal, setShowPacketModal] = useState(false);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const slugFromPath = pathParts[pathParts.length - 1];
    const finalSlug = slug || slugFromPath;
    if (finalSlug && finalSlug !== 'events') {
      fetchEventDetails(finalSlug);
    } else {
      setError("No event slug provided");
      setLoading(false);
    }
  }, [slug, location.pathname]);

  const fetchEventDetails = async (eventSlug: string) => {
    try {
      setLoading(true);
      // Support lookup by either UUID or slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(eventSlug);
      const selectFields = `
          id,
          slug,
          name,
          description,
          start_date,
          end_date,
          registration_close_date,
          status,
          thumbnail_url,
          background_image_url,
          gender,
          sport_id,
          main_venue_id,
          additional_venue_ids,
          check_in_instructions_html,
          venue_name,
          venue_city,
          venue_state,
          venue_address,
          venue_zip,
          promo_video_url,
          age_groups_description,
          pricing_notes,
          event_format,
          hotel_booking_url,
          live_streaming_url,
          rules,
          media_credentials_description,
          media_credentials_form_id,
          media_credentials_notification_email,
          ticket_display_text,
          coaches_external_url,
          more_info,
          coach_attendance_fee,
          accept_online_registrations,
          external_registration_url,
          gallery_images,
          media_credentials_price,
          is_ncaa_live_event,
          ncaa_d1_first_coach_fee,
          ncaa_d1_additional_coach_fee,
          ncaa_other_first_coach_fee,
          ncaa_other_additional_coach_fee,
          ncaa_hospitality_days,
          coach_coupon_code,
          ncaa_coupon_code,
          ncaa_coupon_discount_type,
          ncaa_coupon_discount_value,
          coach_registration_instructions,
          event_category_mappings!event_category_mappings_event_id_fkey(
            event_categories!event_category_mappings_event_category_id_fkey(id, name)
          ),
          event_pricing:event_pricing(pricing_structure)
      `;
      const query = isUuid
        ? supabase.from('events').select(selectFields).eq('id', eventSlug).maybeSingle()
        : supabase.from('events').select(selectFields).eq('slug', eventSlug).maybeSingle();

      const { data, error } = await query;

      if (error) throw error;
      if (!data) {
        setError("Event not found");
        setLoading(false);
        return;
      }

      let mainVenue: Venue | undefined;
      if (data.main_venue_id) {
        const { data: mainVenueData } = await supabase
          .from('venues')
          .select('id, name, address, city, state, zip_code')
          .eq('id', data.main_venue_id)
          .maybeSingle();
        if (mainVenueData) mainVenue = mainVenueData;
      }

      let additionalVenues: Venue[] = [];
      if (Array.isArray(data.additional_venue_ids) && data.additional_venue_ids.length > 0) {
        const { data: venuesData } = await supabase
          .from('venues')
          .select('id, name, address, city, state, zip_code')
          .in('id', data.additional_venue_ids);
        if (venuesData) additionalVenues = venuesData;
      }

      // Extract pricing data from new JSONB structure
      const pricingStructure = Array.isArray(data.event_pricing) && data.event_pricing.length > 0
        ? data.event_pricing[0].pricing_structure
        : {};

      // Transform division_pricing to age_groups format (for team events) and sort numerically
      const unsortedAgeGroups: { id: string; name: string; registration_fee: string }[] = (pricingStructure.division_pricing || []).map((div: any) => ({
        id: div.id || `div-${div.name}`,
        name: div.name,
        registration_fee: ((div.price || 0) / 100).toFixed(2) // Convert cents to dollars
      }));
      const age_groups = sortDivisions(unsortedAgeGroups);

      // Transform registration_tiers to pricing_tiers format (for individual events)
      const pricing_tiers = (pricingStructure.registration_tiers || []).map((tier: any) => ({
        id: tier.id || `tier-${tier.tier_name}`,
        tier_name: tier.tier_name,
        price: ((tier.price || 0) / 100).toFixed(2) // Convert cents to dollars
      }));

      const transformedEvent: EventData = {
        id: data.id,
        slug: data.slug,
        name: data.name,
        description: data.description,
        start_date: data.start_date,
        end_date: data.end_date,
        registration_close_date: data.registration_close_date,
        status: data.status,
        thumbnail_url: data.thumbnail_url,
        background_image_url: data.background_image_url,
        gender: data.gender,
        sport_id: data.sport_id,
        venue_name: data.venue_name,
        venue_city: data.venue_city,
        venue_state: data.venue_state,
        venue_address: data.venue_address,
        venue_zip: data.venue_zip,
        main_venue_id: data.main_venue_id,
        additional_venue_ids: data.additional_venue_ids,
        check_in_instructions_html: (data as any).checkin_instructions,
        promo_video_url: data.promo_video_url,
        age_groups_description: data.age_groups_description,
        pricing_notes: data.pricing_notes,
        event_format: data.event_format,
        hotel_booking_url: data.hotel_booking_url,
        live_streaming_url: data.live_streaming_url,
        rules: data.rules, // Use rules directly
        media_credentials_description: data.media_credentials_description,
        media_credentials_form_id: data.media_credentials_form_id,
        media_credentials_notification_email: data.media_credentials_notification_email,
        ticket_display_text: data.ticket_display_text,
        coaches_external_url: data.coaches_external_url,
        more_info: data.more_info,
        accept_online_registrations: data.accept_online_registrations,
        external_registration_url: data.external_registration_url,
        coach_attendance_fee: data.coach_attendance_fee,
        media_credentials_price: data.media_credentials_price,
        gallery_images: data.gallery_images || [],
        main_venue: mainVenue,
        additional_venues: additionalVenues,
        event_category_mappings: (data.event_category_mappings || []).map((mapping) => ({
          event_categories: Array.isArray(mapping.event_categories)
            ? mapping.event_categories[0]
            : mapping.event_categories,
        })),
        age_groups: age_groups,
        pricing_tiers: pricing_tiers,
        // NCAA Live fields
        is_ncaa_live_event: data.is_ncaa_live_event,
        ncaa_d1_first_coach_fee: data.ncaa_d1_first_coach_fee,
        ncaa_d1_additional_coach_fee: data.ncaa_d1_additional_coach_fee,
        ncaa_other_first_coach_fee: data.ncaa_other_first_coach_fee,
        ncaa_other_additional_coach_fee: data.ncaa_other_additional_coach_fee,
        ncaa_hospitality_days: data.ncaa_hospitality_days,
        coach_coupon_code: data.coach_coupon_code,
        ncaa_coupon_code: data.ncaa_coupon_code,
        ncaa_coupon_discount_type: data.ncaa_coupon_discount_type as 'percentage' | 'fixed' | undefined,
        ncaa_coupon_discount_value: data.ncaa_coupon_discount_value,
        coach_registration_instructions: data.coach_registration_instructions,
      };

      setEvent(transformedEvent);
      
      // Fetch sponsors using event ID
      if (data.id) {
        await fetchEventSponsors(data.id);
        // Fetch published packet for college coaches
        await fetchPublishedPacket(data.id);
      }
    } catch (err: any) {
      console.error('Error fetching event details:', err);
      setError(err.message || 'Failed to fetch event details');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventSponsors = async (eventId: string) => {
    try {
      const { data, error } = await supabase
        .from('event_sponsor_flat_view')
        .select('sponsor_id, name, logo_url, website_url')
        .eq('event_id', eventId);

      if (error) {
        console.error('Error fetching sponsors:', error);
        return;
      }

      const sponsorData: EventSponsor[] = (data || []).map((row) => ({
        id: row.sponsor_id,
        name: row.name,
        logo_url: row.logo_url,
        website_url: row.website_url,
      }));

      setSponsors(sponsorData);
    } catch (err: any) {
      console.error('Error in fetchEventSponsors:', err);
    }
  };

  const fetchPublishedPacket = async (eventId: string) => {
    try {
      const { data, error } = await supabase
        .from('generated_packets')
        .select('id, packet_price_cents, packet_file_url')
        .eq('event_id', eventId)
        .eq('is_published', true)
        .maybeSingle();

      if (!error && data) {
        setPublishedPacket(data);
      }
    } catch (err) {
      console.error('Error fetching published packet:', err);
    }
  };

  const getImageUrl = (filename: string | null | undefined) => {
    if (!filename) return null;
    if (filename.startsWith('http')) return filename;
    return `https://pvaumvowzncvrdkkvnqq.supabase.co/storage/v1/object/public/event-featured/${filename}`;
  };

  const renderPromoVideo = (promoVideoUrl: string) => {
    // YouTube: click-to-play thumbnail; iframe only mounts on click
    if (parseYouTubeId(promoVideoUrl)) {
      return <EventPromoVideo url={promoVideoUrl} />;
    }

    // If it's already an iframe, extract src and use it
    if (promoVideoUrl.includes('<iframe')) {
      const src = extractSrcFromIframe(promoVideoUrl);
      if (src) {
        return (
          <div className="aspect-video w-full overflow-hidden rounded-lg">
            <iframe
              width="100%"
              height="100%"
              src={src}
              title="Event Promo Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ aspectRatio: '16/9', width: '100%', height: 'auto' }}
            />
          </div>
        );
      }
    }
    
    // If it's a direct video URL, try to embed it
    if (promoVideoUrl.includes('.mp4') || promoVideoUrl.includes('.webm') || promoVideoUrl.includes('.ogg')) {
      return (
        <div className="aspect-video w-full overflow-hidden rounded-lg">
          <video
            width="100%"
            height="100%"
            controls
            style={{ aspectRatio: '16/9', width: '100%', height: 'auto' }}
          >
            <source src={promoVideoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }
    
    // Enhanced fallback with YouTube thumbnail if possible
    return (
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM5 8a1 1 0 000 2h8a1 1 0 100-2H5z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">Video available</p>
          <a 
            href={promoVideoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Watch Video
          </a>
        </div>
      </div>
    );
  };

  const renderSponsors = () => {
    if (!sponsors || sponsors.length === 0) return null;

    return (
      <div className="rounded-xl bg-white shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Event Sponsors</h2>
        <div className="flex flex-wrap gap-8 items-center">
          {sponsors.map((sponsor) => (
            <SponsorLogo
              key={sponsor.id}
              sponsor={sponsor}
              placement="event_page"
              eventId={event?.id}
              className="flex items-center justify-center"
            />
          ))}
        </div>
      </div>
    );
  };

  const { data: scheduleTabs = [] } = useVisibleScheduleTabs(event?.id);

  if (loading) return <div>Loading...</div>;
  if (error || !event) return <div>Error: {error}</div>;

  const hasScheduleInfo = scheduleTabs.length > 0;

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {/* Mobile Sticky Register Button */}
      {isMobile && event && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 p-3 shadow-sm">
          <Button 
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3"
            onClick={() => {
              if (!event.accept_online_registrations && event.external_registration_url) {
                window.open(event.external_registration_url, '_blank');
              } else {
                window.location.href = `/events/${event.slug}/register`;
              }
            }}
          >
            Register Now - From ${(() => {
              const prices: number[] = [];
              
              // Get prices from age groups
              if (event.age_groups && event.age_groups.length > 0) {
                event.age_groups.forEach(group => {
                  const price = parseFloat(group.registration_fee);
                  if (!isNaN(price) && price > 0) {
                    prices.push(price);
                  }
                });
              }
              
              // Get prices from pricing tiers
              if (event.pricing_tiers && event.pricing_tiers.length > 0) {
                event.pricing_tiers.forEach(tier => {
                  const price = parseFloat(tier.price);
                  if (!isNaN(price) && price > 0) {
                    prices.push(price);
                  }
                });
              }
              
              if (prices.length === 0) {
                return 99; // Default fallback price
              }
              
              return Math.min(...prices);
            })()}
          </Button>
        </div>
      )}

      {/* Add top padding on mobile to account for sticky button */}
      <div className={isMobile ? "pt-20" : ""}>
        {event?.id && <EventVerificationNotice eventId={event.id} className="mb-6" />}

        <div className={`flex gap-8 ${isMobile ? 'flex-col' : 'flex-col lg:flex-row'}`}>
          {/* Mobile: Sidebar First, Desktop: Main Content First */}
          {isMobile ? (
            <>
              {/* Mobile Sidebar First */}
              <div className="w-full">
                <EventSidebar event={event} />
              </div>
              
              {/* Mobile Main Content Second */}
              <div className="w-full">
                {/* Event Title and Description */}
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md mb-6">
                  <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">{event.name}</h1>
                    
                    {/* Category Badges */}
                    {event.event_category_mappings && event.event_category_mappings.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {event.event_category_mappings.map((mapping, index) => (
                          <Badge 
                            key={mapping.event_categories.id || index} 
                            variant="secondary"
                            className="text-xs"
                          >
                            {mapping.event_categories.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div 
                      className="text-sm sm:text-base [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
                      dangerouslySetInnerHTML={{ __html: event.description || '' }} 
                    />
                  </div>
                </div>

                {event?.promo_video_url && (
                  <div className="rounded-xl bg-white shadow p-4 mb-6">
                    <h2 className="text-lg font-semibold mb-4">🎥 Event Video</h2>
                    {renderPromoVideo(event.promo_video_url)}
                  </div>
                )}

                {renderSponsors()}

                {/* Event Gallery Section */}
                {event.gallery_images && event.gallery_images.length > 0 && (
                  <div className="rounded-xl bg-white shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">📸 Event Gallery</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {event.gallery_images.map((imageUrl, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={imageUrl}
                            alt={`${event.name} gallery image ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300 cursor-pointer"
                            onClick={() => window.open(imageUrl, '_blank')}
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <EventHelpfulInformation event={event} />

                <OfficialHotelsSection eventId={event.id} />

                {/* Add Event FAQs Section */}
                <EventFAQSection eventId={event.id} />
              </div>
            </>
          ) : (
            <>
              {/* Desktop: Sidebar First (Left) */}
              <div className="lg:w-80">
                <EventSidebar event={event} />
              </div>

              {/* Desktop: Main Content Second */}
              <div className="flex-1">
                {/* Event Title and Description */}
                <div className="bg-white rounded-xl p-6 shadow-md mb-6">
                  <div>
                    <h1 className="text-3xl font-bold mb-2">{event.name}</h1>
                    
                    {/* Category Badges */}
                    {event.event_category_mappings && event.event_category_mappings.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {event.event_category_mappings.map((mapping, index) => (
                          <Badge 
                            key={mapping.event_categories.id || index} 
                            variant="secondary"
                            className="text-xs"
                          >
                            {mapping.event_categories.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="[&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: event.description || '' }} />
                  </div>
                </div>

                {event?.promo_video_url && (
                  <div className="rounded-xl bg-white shadow p-4 mb-6">
                    <h2 className="text-lg font-semibold mb-4">🎥 Event Video</h2>
                    {renderPromoVideo(event.promo_video_url)}
                  </div>
                )}

                {renderSponsors()}

                {/* Event Gallery Section */}
                {event.gallery_images && event.gallery_images.length > 0 && (
                  <div className="rounded-xl bg-white shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">📸 Event Gallery</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {event.gallery_images.map((imageUrl, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={imageUrl}
                            alt={`${event.name} gallery image ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300 cursor-pointer"
                            onClick={() => window.open(imageUrl, '_blank')}
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <EventHelpfulInformation event={event} />

                <OfficialHotelsSection eventId={event.id} />

                {/* Coach Packet Section - College Coaches Only */}
                {(profile?.role === 'college_coach' || profile?.role === 'admin') && publishedPacket && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Coach Packet
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        Get the complete roster packet with player profiles and contact information.
                      </p>
                      <Button onClick={() => setShowPacketModal(true)} className="w-full">
                        Purchase Packet - ${(publishedPacket.packet_price_cents / 100).toFixed(2)}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {/* Add Event FAQs Section */}
                <EventFAQSection eventId={event.id} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {hasScheduleInfo && (
        <EventScheduleModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          eventName={event.name}
          tabs={scheduleTabs}
        />
      )}

      {/* Coach Packet Purchase Modal */}
      <PurchasePacketsModal
        open={showPacketModal}
        onOpenChange={setShowPacketModal}
        packet={publishedPacket}
        eventId={event.id}
        eventName={event.name}
      />
    </div>
  );
};

export default EventDetailPage;
